import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Activity, KeyRound, LogOut, MoreVertical, Play, Sun, Moon } from 'lucide-react'
import { systemApi, engineApi, auditApi } from '../api/client'
import { useTheme } from '../hooks/useTheme'
import { useAuth } from '../auth/AuthContext'
import { StopEngineButton } from './StopEngineButton'
import { MobileTimeframeRow } from './MobileTimeframeRow'
import type { WebSocketHookReturn } from '../hooks/useWebSocket'
import type { EngineStatus } from '../types'
import './MobileHeader.css'

/**
 * Structurally identical to AppShell's local `runStatus` state shape.
 * Not imported from AppShell on purpose — AppShell's desktop JSX region
 * (the fragment ternary's untouched half) must stay byte-identical, so we
 * avoid threading a new named export through it. TypeScript's structural
 * typing makes the duplication safe: the value AppShell passes down just
 * needs to match this shape, not literally be this type.
 */
type RunStatus =
  | { kind: 'idle' }
  | { kind: 'success'; runId: string; durationMs: number }
  | { kind: 'ran-failed'; runId: string; error: string }
  | { kind: 'error'; message: string }

export interface MobileHeaderProps {
  ws: WebSocketHookReturn
  engineStatus: EngineStatus | undefined
  marketClosed: boolean
  marketStatus: string | undefined
  runStatus: RunStatus
  onRunWorkflow: () => void
  isRunning: boolean
}

type OpenPanel = 'none' | 'details' | 'menu'

/**
 * Compact sticky header for the mobile shell (<=767px).
 *
 * MobileTimeframeRow renders as the LAST child inside this <header>
 * (rather than as a sibling sticky element of its own) so the two share
 * one sticky block. The row previously pinned itself with
 * `position: sticky; top: <fixed header height>`, which broke whenever
 * this header grew taller than that constant — an alarm band or the
 * expanded details/menu panel would occlude the row instead of pushing it
 * down. Nesting it here means it just flows after whatever this header is
 * currently showing, inside the header's own sticky box, so there is no
 * fixed offset to go stale.
 *
 * SAFETY LOCK (WARGAME 18 D2): this component runs its own copies of all
 * three SafetyStrip queries (system-health, engine-status, auto-stop-status)
 * at the same 10s refetchInterval and the same query keys as SafetyStrip —
 * SafetyStrip itself does not render at this width (F15/F17). Sharing query
 * keys means both components' observers merge into one react-query cache
 * entry, so mounting this at 10s keeps auto-stop visibility fresh even
 * though SafetyStrip is unmounted; it does NOT read the desktop chrome's
 * 60s engine-status/system-health cache (that would silently go stale).
 *
 * Alarm rendering is locked: `engineStatus.status !== 'running'` and
 * `autostop.tripped` (mirroring SafetyStrip.tsx's own derivation exactly)
 * both render as full-width red labeled bands in the always-visible
 * ("collapsed") part of the header — never hidden behind the details tap.
 */
export function MobileHeader({
  ws,
  engineStatus,
  marketClosed,
  marketStatus,
  runStatus,
  onRunWorkflow,
  isRunning,
}: MobileHeaderProps) {
  const [openPanel, setOpenPanel] = useState<OpenPanel>('none')
  const { theme, toggle: toggleTheme } = useTheme()
  const { user, logout } = useAuth()

  const health = useQuery({
    queryKey: ['system-health'],
    queryFn: systemApi.getHealth,
    refetchInterval: 10000,
  })
  const engine = useQuery({
    queryKey: ['engine-status'],
    queryFn: engineApi.getStatus,
    refetchInterval: 10000,
  })
  const autostop = useQuery({
    queryKey: ['auto-stop-status'],
    queryFn: auditApi.autoStopStatus,
    refetchInterval: 10000,
  })

  // Mirrors SafetyStrip.tsx's derivation exactly (F15's sole-consumer note
  // means this is now a second consumer of the same query key/shape).
  const tripped = autostop.data?.available === true && autostop.data?.tripped === true
  // Quiet while unresolved, not loud: gating on `engine.data !== undefined`
  // means an in-flight query (first paint) AND a query that's rejecting
  // (API down) both render NO alarm band, rather than treating "we don't
  // know yet" the same as "confirmed not running". A loud red band is the
  // wrong response to our own fetch failing — that's cry-wolf, not safety.
  // Degradation isn't hidden, though: `allHealthy` below folds in this same
  // `engine.data !== undefined` check, so the compact status dot still
  // renders unhealthy (red) and `engineLabel`/`marketLabel` fall back to an
  // em-dash rather than a fabricated status string. See the "engine query
  // rejects" case in MobileHeader.test.tsx, which pins this choice.
  const engineTripped = engine.data !== undefined && engine.data.status !== 'running'
  const engineLabel = engine.data?.status ?? '—'
  const marketLabel = health.data?.market_status ?? '—'
  const autoStopLabel = autostop.data?.available ? (tripped ? 'TRIPPED' : 'armed') : 'unknown'
  const allHealthy = health.data?.system_status === 'running' && !engineTripped && !tripped

  const autonomousMode =
    engineStatus?.status === 'running' &&
    (engineStatus.workflow_mode === 'scheduled' || engineStatus.workflow_mode === 'continuous')

  const toggleDetails = () =>
    setOpenPanel((prev) => (prev === 'details' ? 'none' : 'details'))
  const toggleMenu = () => setOpenPanel((prev) => (prev === 'menu' ? 'none' : 'menu'))

  const handleRunWorkflow = () => {
    // Market-closed guard preserved verbatim from AppShell's desktop
    // handler (AppShell.tsx) — the mutate/confirm split is what D2's
    // "reusing the passed-in confirm/mutation props" refers to: the parent
    // owns the mutation, this component owns the confirm branch.
    if (marketClosed) {
      const ok = window.confirm(
        `Market is currently ${marketStatus}. Running a workflow ` +
        `cycle will execute the full pipeline but most signals ` +
        `will be rejected on stale_data and no orders will fill ` +
        `until the market reopens.\n\nRun anyway?`,
      )
      if (!ok) return
    }
    onRunWorkflow()
  }

  return (
    <header className="mobile-header">
      <div className="mobile-header-row">
        <span className="mobile-header-brand" aria-hidden="true">
          <Activity size={20} />
        </span>

        <button
          type="button"
          className="mobile-status-chip-btn"
          onClick={toggleDetails}
          aria-expanded={openPanel === 'details'}
          aria-controls="mobile-header-details"
        >
          <span className="mobile-status-dot" data-ok={allHealthy} aria-hidden="true" />
          <span className="mobile-status-chip-label">
            Engine {engineLabel} · Market {marketLabel}
          </span>
        </button>

        <span
          className="mobile-ws-dot"
          data-connected={ws.isConnected}
          role="img"
          aria-label={ws.isConnected ? 'WebSocket connected' : 'WebSocket disconnected'}
          title={ws.isConnected ? 'Connected' : 'Disconnected'}
        />

        <button
          type="button"
          className="mobile-overflow-btn"
          onClick={toggleMenu}
          aria-haspopup="menu"
          aria-expanded={openPanel === 'menu'}
          aria-label="More options"
        >
          <MoreVertical size={20} aria-hidden="true" />
        </button>
      </div>

      {engineTripped && (
        <button
          type="button"
          className="mobile-alarm-band"
          onClick={toggleDetails}
          aria-expanded={openPanel === 'details'}
        >
          ENGINE {engineLabel.toUpperCase()}
        </button>
      )}
      {tripped && (
        <button
          type="button"
          className="mobile-alarm-band mobile-alarm-band--critical"
          onClick={toggleDetails}
          aria-expanded={openPanel === 'details'}
        >
          AUTO-STOP TRIPPED
        </button>
      )}

      {openPanel === 'details' && (
        <div className="mobile-header-expanded" id="mobile-header-details">
          <dl className="mobile-strip-facts">
            <div>
              <dt>Engine</dt>
              <dd>{engineLabel}</dd>
            </div>
            <div>
              <dt>Market</dt>
              <dd>{marketLabel}</dd>
            </div>
            <div>
              <dt>Auto-stop</dt>
              <dd>{autoStopLabel}</dd>
            </div>
          </dl>
          <StopEngineButton />
        </div>
      )}

      {openPanel === 'menu' && (
        <div className="mobile-overflow-menu" role="menu">
          <button
            type="button"
            role="menuitem"
            className="mobile-overflow-item"
            onClick={handleRunWorkflow}
            disabled={isRunning}
          >
            <Play size={16} aria-hidden="true" />
            {isRunning ? 'Running…' : 'Run Workflow'}
          </button>
          {autonomousMode && (
            <p className="mobile-overflow-caption">
              Auto · {engineStatus?.workflow_mode}
            </p>
          )}
          {runStatus.kind === 'success' && (
            <p className="mobile-overflow-caption mobile-overflow-caption--ok">
              {runStatus.runId} ({(runStatus.durationMs / 1000).toFixed(2)}s)
            </p>
          )}
          {runStatus.kind === 'ran-failed' && (
            <p className="mobile-overflow-caption mobile-overflow-caption--err">
              Cycle ran, failed: {runStatus.error} ({runStatus.runId})
            </p>
          )}
          {runStatus.kind === 'error' && (
            <p className="mobile-overflow-caption mobile-overflow-caption--err">
              Server error: {runStatus.message}
            </p>
          )}

          <Link
            role="menuitem"
            to="/agent-tokens"
            className="mobile-overflow-item"
            onClick={() => setOpenPanel('none')}
          >
            <KeyRound size={16} aria-hidden="true" />
            Agent Tokens
          </Link>

          <button
            type="button"
            role="menuitem"
            className="mobile-overflow-item"
            onClick={toggleTheme}
          >
            {theme === 'dark' ? <Sun size={16} aria-hidden="true" /> : <Moon size={16} aria-hidden="true" />}
            {theme === 'dark' ? 'Light theme' : 'Dark theme'}
          </button>

          <button
            type="button"
            role="menuitem"
            className="mobile-overflow-item"
            onClick={() => {
              setOpenPanel('none')
              void logout()
            }}
          >
            <LogOut size={16} aria-hidden="true" />
            Sign out{user ? ` (${user.username})` : ''}
          </button>
        </div>
      )}

      <MobileTimeframeRow />
    </header>
  )
}
